import { useQuery } from '@sixfold/app-data-framework';
import { lazyWithRetry } from '@sixfold/app-utils';
import { SpinnerOverlay } from '@sixfold/common-ui';
import { notNil } from '@sixfold/typed-primitives';
import React, { Suspense } from 'react';
import { useParams, Route, Routes as Switch } from 'react-router';

import { ViewerCompanyContainer } from '../company';
import { sessionViewerQuery } from './graphql';
import { SessionViewerQuery, SessionViewerQueryVariables } from '../lib/graphql';
import { useLastViewedContext } from '../lib/util/use_last_viewed_context';
import { useTrackLoginTypeEffect } from '../lib/util/use_track_login_type_effect';
import { Routes } from '../routes';
import { HomeRedirectRoute } from '../routes/home_redirect';

const SupportedCompanyRoute = lazyWithRetry(() =>
  import('../routes/supported_company').then(({ SupportedCompanyRoute }) => ({ default: SupportedCompanyRoute })),
);
const GoRedirectRoute = lazyWithRetry(() =>
  import('../routes/go_redirect').then(({ GoRedirectRoute }) => ({ default: GoRedirectRoute })),
);
const ExternalRedirectRoute = lazyWithRetry(() =>
  import('../routes/external_redirect').then(({ ExternalRedirectRoute }) => ({ default: ExternalRedirectRoute })),
);

interface Props {
  logOut: () => void;
}

export const App: React.FunctionComponent<React.PropsWithChildren<Props>> = ({ logOut }) => {
  useTrackLoginTypeEffect();
  return (
    <Switch>
      <Route
        key={Routes.ExternalRedirect.routerPath}
        path={Routes.ExternalRedirect.routerPath}
        element={
          <Suspense fallback={<SpinnerOverlay />}>
            <ExternalRedirectRoute />
          </Suspense>
        }
      />
      <Route
        key={Routes.GoRedirect.routerPath}
        path={Routes.GoRedirect.routerPath}
        element={
          <ViewerCompanyContainer>
            {({ company_id }) => (
              <Suspense fallback={<SpinnerOverlay />}>
                <GoRedirectRoute companyId={company_id} />
              </Suspense>
            )}
          </ViewerCompanyContainer>
        }
      />
      <Route path={Routes.Company.routerPath} element={<CompanyRoute />} />
      <Route
        path={Routes.Supported.routerPath}
        element={
          <Suspense fallback={<SpinnerOverlay />}>
            <SupportedCompanyRoute logOut={logOut} />
          </Suspense>
        }
      />
      <Route path="*" element={<RootRedirect />} />
    </Switch>
  );
};

const RootRedirect: React.FunctionComponent = () => {
  const { loading, data } = useQuery<SessionViewerQuery, SessionViewerQueryVariables>(sessionViewerQuery);
  const [lastViewedContext] = useLastViewedContext();

  if (loading) {
    return <SpinnerOverlay />;
  }

  const hasCompanyAccess = notNil(data?.viewer);
  const hasSharedViewAccess = notNil(data?.sharedViewViewer);

  if (hasSharedViewAccess && (!hasCompanyAccess || lastViewedContext === 'shared-view')) {
    return <SharedViewRedirect />;
  }

  return (
    <ViewerCompanyContainer>
      {({ company_id }) => (
        <HomeRedirectRoute companyId={company_id} defaultRoutePath={Routes.DataNetwork.generatePath({ company_id })} />
      )}
    </ViewerCompanyContainer>
  );
};

const SharedViewRedirect: React.FunctionComponent = () => {
  React.useEffect(() => {
    window.location.href = Routes.PublicSharedViewLanding.generatePath({});
  }, []);

  return <SpinnerOverlay />;
};

const CompanyRoute = () => {
  const { company_id } = useParams<Routes.Company>();
  const validCompanyId = company_id || '';
  return (
    <HomeRedirectRoute
      companyId={validCompanyId}
      defaultRoutePath={Routes.Transports.generatePath({ company_id: validCompanyId })}
    />
  );
};
